import { lowercaseFirstLetter, uppercaseFirstLetter } from '~/utils/helpers';

import { ExportType, FilterField } from '../types/api.graphql';

/* ***********************************************************
 * IMPORTANT: It's important to keep the order of the columns
 * unchanged. This is because the S3 integration relies on
 * this order.
 *********************************************************** */

export const COLUMN_NAMES = {
  IP_LOCATION: 'ipLocation',
  UTM_CAMPAIGN: 'utmCampaign',
  UTM_MEDIUM: 'utmMedium',
  UTM_SOURCE: 'utmSource',
  GA_SESSION_ID: 'gaSessionId',
  TEALIUM_VISITOR_ID: 'tealiumVisitorId',
  NAME: 'name',
  SOCIAL: 'social',
  EMAIL: 'email',
  EMAIL_VERIFICATION_DATE: 'emailVerificationDate',
  BUSINESS_EMAIL: 'businessEmail',
  OPT_IN: 'optIn',
  OPT_IN_DATE: 'optInDate',
  OPT_IN_IP: 'optInIp',
  OPT_IN_URL: 'optInUrl',
  PIXEL_FIRST_HIT_DATE: 'pixelFirstHitDate',
  PIXEL_LAST_HIT_DATE: 'pixelLastHitDate',
  PIXEL_LAST_IP_LOCATION: 'pixelLastIpLocation',
  BE_BACKS: 'bebacks',
  PHONE: 'phone',
  DNC: 'dnc',
  AGE: 'age',
  GENDER: 'gender',
  MARITAL_STATUS: 'maritalStatus',
  ADDRESS: 'address',
  CITY: 'city',
  STATE: 'state',
  ZIP: 'zip',
  IS_PRIMARY_RESIDENCE: 'isPrimaryResidence',
  HOUSEHOLD_INCOME: 'householdIncome',
  NET_WORTH: 'netWorth',
  INCOME_LEVELS: 'incomeLevels',
  PEOPLE_IN_HOUSEHOLD: 'peopleInHousehold',
  ADULTS_IN_HOUSEHOLD: 'adultsInHousehold',
  CHILDREN_IN_HOUSEHOLD: 'childrenInHousehold',
  VETERANS_IN_HOUSEHOLD: 'veteransInHousehold',
  EDUCATION: 'education',
  CREDIT_RANGE: 'creditRange',
  ETHNIC_GROUP: 'ethnicGroup',
  GENERATION: 'generation',
  HOME_OWNER: 'homeOwner',
  OCCUPATION_DETAIL: 'occupationDetail',
  POLITICAL_PARTY: 'politicalParty',
  RELIGION: 'religion',
  CHILDREN_BETWEEN_AGES_0_3: 'childrenBetweenAges0_3',
  CHILDREN_BETWEEN_AGES_4_6: 'childrenBetweenAges4_6',
  CHILDREN_BETWEEN_AGES_7_9: 'childrenBetweenAges7_9',
  CHILDREN_BETWEEN_AGES_10_12: 'childrenBetweenAges10_12',
  CHILDREN_BETWEEN_AGES_13_18: 'childrenBetweenAges13_18',
  BEHAVIORS: 'behaviors',
  CHILDREN_AGE_RANGES: 'childrenAgeRanges',
  INTERESTS: 'interests',
  OWNS_AMEX_CARD: 'ownsAmexCard',
  OWNS_BANK_CARD: 'ownsBankCard',
  DWELLING_TYPE: 'dwellingType',
  HOME_HEAT_TYPE: 'homeHeatType',
  HOME_PRICE: 'homePrice',
  HOME_PURCHASED_YEARS_AGO: 'homePurchasedYearsAgo',
  HOME_VALUE: 'homeValue',
  HOUSEHOLD_NET_WORTH: 'householdNetWorth',
  LANGUAGE: 'language',
  MORTGAGE_AGE: 'mortgageAge',
  MORTGAGE_AMOUNT: 'mortgageAmount',
  MORTGAGE_LOAN_TYPE: 'mortgageLoanType',
  MORTGAGE_REFINANCE_AGE: 'mortgageRefinanceAge',
  MORTGAGE_REFINANCE_AMOUNT: 'mortgageRefinanceAmount',
  MORTGAGE_REFINANCE_TYPE: 'mortgageRefinanceType',
  IS_MULTILINGUAL: 'isMultilingual',
  NEW_CREDIT_OFFERED_HOUSEHOLD: 'newCreditOfferedHousehold',
  NUMBER_OF_VEHICLES_IN_HOUSEHOLD: 'numberOfVehiclesInHousehold',
  OWNS_INVESTMENT: 'ownsInvestment',
  OWNS_PREMIUM_AMEX_CARD: 'ownsPremiumAmexCard',
  OWNS_PREMIUM_CARD: 'ownsPremiumCard',
  OWNS_STOCKS_AND_BONDS: 'ownsStocksAndBonds',
  PERSONALITY: 'personality',
  IS_POLITICAL_CONTRIBUTOR: 'isPoliticalContributor',
  PREMIUM_INCOME_HOUSEHOLD: 'premiumIncomeHousehold',
  URBANICITY: 'urbanicity',
  MAID: 'maid',
  MAID_OS: 'maidOs',
  ZIP4: 'zip4',
  DPBC: 'dpbc',
  DPV: 'dpv',
  CARRIER_ROUTE: 'carrierRoute',
  FIPS_STATE_CODE: 'fipsStateCode',
  FIPS_COUNTY_CODE: 'fipsCountyCode',
  COUNTY_NAME: 'countyName',
  COMPANY_NAME: 'companyName',
  COMPANY_REVENUE: 'companyRevenue',
  SENIORITY_LEVEL: 'seniorityLevel',
  DEPARTMENT: 'department',
  JOB_TITLE: 'jobTitle',
  PRIMARY_INDUSTRY: 'primaryIndustry',
  COMPANY_CITY: 'companyCity',
  COMPANY_STATE: 'companyState',
  COMPANY_DOMAIN: 'companyDomain',
  COMPANY_EMPLOYEE_COUNT: 'companyEmployeeCount',
  COMPANY_SIC: 'companySic',
  DMA: 'dma',
  BEESWAX_IDS: 'beeswaxIds',
  IP_ADDRESSES: 'ipAddresses',
  MAIDS: 'maids',
  HEMS_MD5: 'hemsMd5',
  HEMS_SHA1: 'hemsSha1',
  HEMS_SHA256: 'hemsSha256',
  HEMS_MD5_UPPERCASE: 'hemsMd5Uppercase',
  HEMS_SHA1_UPPERCASE: 'hemsSha1Uppercase',
  HEMS_SHA256_UPPERCASE: 'hemsSha256Uppercase',
  // Matchback only: which of its comparisons a person matched through. Left out of `allColumns`
  // (below), so it never shows up in another audience's "Customize columns" list or shared
  // storage; usePeopleColumns adds it back for a Matchback.
  MATCHED_BY: 'matchedBy',
  // Customer List (with matching rules) only: which of the list's matching rules brought the person in. Left out of
  // `allColumns` like MATCHED_BY; usePeopleColumns adds it back for an audience that supports matching rules.
  MATCHING_RULE: 'matchingRule',
} as const;

export type ColumnType = typeof COLUMN_NAMES[keyof typeof COLUMN_NAMES];

export enum ColumnKey {
  FullName = 'fullName',
  FirstName = 'firstName',
  LastName = 'lastName',
  IPLocation = 'ipLocation',
  UtmCampaign = 'utmCampaign',
  UtmMedium = 'utmMedium',
  UtmSource = 'utmSource',
  GaSessionId = 'gaSessionId',
  TealiumVisitorId = 'tealiumVisitorId',
}

export interface ColumnData {
  key: string;
  excludeInExport?: ExportType[];
  filterField?: FilterField | FilterField[];
  excludeSorting?: boolean;
  pixelOnly?: boolean;
  tooltipKeyText?: string;
}

const columnsToRecord = (columns: ColumnType[], props: Omit<ColumnData, 'key' | 'filterField'> = undefined) => {
  return columns.reduce((result, column) => {
    result[column] = {
      key: lowercaseFirstLetter(column),
      filterField: FilterField[uppercaseFirstLetter(column)],
      ...props,
    };
    return result;
  }, {} as Record<ColumnType, ColumnData>);
};

export const columnsData: Record<ColumnType, ColumnData> = {
  [COLUMN_NAMES.NAME]: {
    key: ColumnKey.FullName,
    filterField: [FilterField.FirstName, FilterField.LastName],
  },

  // sort enabled
  ...columnsToRecord([
    COLUMN_NAMES.AGE,
    COLUMN_NAMES.ADULTS_IN_HOUSEHOLD,
    COLUMN_NAMES.CHILDREN_IN_HOUSEHOLD,
    COLUMN_NAMES.CHILDREN_AGE_RANGES,
    COLUMN_NAMES.CITY,
    COLUMN_NAMES.CREDIT_RANGE,
    COLUMN_NAMES.DWELLING_TYPE,
    COLUMN_NAMES.EDUCATION,
    COLUMN_NAMES.ETHNIC_GROUP,
    COLUMN_NAMES.GENDER,
    COLUMN_NAMES.GENERATION,
    COLUMN_NAMES.HOME_HEAT_TYPE,
    COLUMN_NAMES.HOME_OWNER,
    COLUMN_NAMES.HOME_PRICE,
    COLUMN_NAMES.HOME_PURCHASED_YEARS_AGO,
    COLUMN_NAMES.HOME_VALUE,
    COLUMN_NAMES.HOUSEHOLD_INCOME,
    COLUMN_NAMES.HOUSEHOLD_NET_WORTH,
    COLUMN_NAMES.INCOME_LEVELS,
    COLUMN_NAMES.LANGUAGE,
    COLUMN_NAMES.MARITAL_STATUS,
    COLUMN_NAMES.MORTGAGE_AGE,
    COLUMN_NAMES.MORTGAGE_AMOUNT,
    COLUMN_NAMES.MORTGAGE_LOAN_TYPE,
    COLUMN_NAMES.MORTGAGE_REFINANCE_AGE,
    COLUMN_NAMES.MORTGAGE_REFINANCE_AMOUNT,
    COLUMN_NAMES.MORTGAGE_REFINANCE_TYPE,
    COLUMN_NAMES.NET_WORTH,
    COLUMN_NAMES.NEW_CREDIT_OFFERED_HOUSEHOLD,
    COLUMN_NAMES.NUMBER_OF_VEHICLES_IN_HOUSEHOLD,
    COLUMN_NAMES.OCCUPATION_DETAIL,
    COLUMN_NAMES.OWNS_INVESTMENT,
    COLUMN_NAMES.OWNS_STOCKS_AND_BONDS,
    COLUMN_NAMES.PEOPLE_IN_HOUSEHOLD,
    COLUMN_NAMES.PERSONALITY,
    COLUMN_NAMES.PHONE,
    COLUMN_NAMES.PREMIUM_INCOME_HOUSEHOLD,
    COLUMN_NAMES.POLITICAL_PARTY,
    COLUMN_NAMES.RELIGION,
    COLUMN_NAMES.STATE,
    COLUMN_NAMES.URBANICITY,
    COLUMN_NAMES.VETERANS_IN_HOUSEHOLD,
    COLUMN_NAMES.DMA,
  ]),

  // sort disabled
  ...columnsToRecord(
    [
      COLUMN_NAMES.ADDRESS,
      COLUMN_NAMES.BEHAVIORS,
      COLUMN_NAMES.CHILDREN_BETWEEN_AGES_0_3,
      COLUMN_NAMES.CHILDREN_BETWEEN_AGES_10_12,
      COLUMN_NAMES.CHILDREN_BETWEEN_AGES_13_18,
      COLUMN_NAMES.CHILDREN_BETWEEN_AGES_4_6,
      COLUMN_NAMES.CHILDREN_BETWEEN_AGES_7_9,
      COLUMN_NAMES.EMAIL,
      COLUMN_NAMES.EMAIL_VERIFICATION_DATE,
      COLUMN_NAMES.IS_PRIMARY_RESIDENCE,
      COLUMN_NAMES.OPT_IN,
      COLUMN_NAMES.OPT_IN_DATE,
      COLUMN_NAMES.OPT_IN_IP,
      COLUMN_NAMES.OPT_IN_URL,
      COLUMN_NAMES.INTERESTS,
      COLUMN_NAMES.DNC,
      COLUMN_NAMES.SOCIAL,
      COLUMN_NAMES.ZIP,
      COLUMN_NAMES.MAID,
      COLUMN_NAMES.MAID_OS,
      COLUMN_NAMES.ZIP4,
      COLUMN_NAMES.DPBC,
      COLUMN_NAMES.DPV,
      COLUMN_NAMES.CARRIER_ROUTE,
      COLUMN_NAMES.FIPS_STATE_CODE,
      COLUMN_NAMES.FIPS_COUNTY_CODE,
      COLUMN_NAMES.COUNTY_NAME,
      COLUMN_NAMES.BEESWAX_IDS,
      COLUMN_NAMES.IP_ADDRESSES,
      COLUMN_NAMES.MAIDS,
      COLUMN_NAMES.HEMS_MD5,
      COLUMN_NAMES.HEMS_SHA1,
      COLUMN_NAMES.HEMS_SHA256,
      COLUMN_NAMES.HEMS_MD5_UPPERCASE,
      COLUMN_NAMES.HEMS_SHA1_UPPERCASE,
      COLUMN_NAMES.HEMS_SHA256_UPPERCASE,
    ],
    { excludeSorting: true },
  ),

  // people field does not match a filter field
  [COLUMN_NAMES.IS_MULTILINGUAL]: { key: 'isMultilingual', filterField: FilterField.Multilingual },
  [COLUMN_NAMES.IS_POLITICAL_CONTRIBUTOR]: {
    key: 'isPoliticalContributor',
    filterField: FilterField.PoliticalContributor,
  },
  [COLUMN_NAMES.OWNS_AMEX_CARD]: { key: 'ownsAmexCard', filterField: FilterField.AmexCard },
  [COLUMN_NAMES.OWNS_BANK_CARD]: { key: 'ownsBankCard', filterField: FilterField.BankCard },
  [COLUMN_NAMES.OWNS_PREMIUM_AMEX_CARD]: { key: 'ownsPremiumAmexCard', filterField: FilterField.PremiumAmexCardOwner },
  [COLUMN_NAMES.OWNS_PREMIUM_CARD]: { key: 'ownsPremiumCard', filterField: FilterField.PremiumCard },

  // pixel only
  ...columnsToRecord([COLUMN_NAMES.PIXEL_LAST_HIT_DATE, COLUMN_NAMES.PIXEL_FIRST_HIT_DATE], { pixelOnly: true }),
  [COLUMN_NAMES.BE_BACKS]: {
    key: 'bebacks',
    pixelOnly: true,
    filterField: FilterField.Bebacks,
    tooltipKeyText: 'beBacksHelpText',
  },

  // IP location
  [COLUMN_NAMES.PIXEL_LAST_IP_LOCATION]: {
    key: 'pixelLastIpLocation',
    pixelOnly: true,
    excludeSorting: true,
  },

  [COLUMN_NAMES.IP_LOCATION]: {
    key: 'ipLocation',
    pixelOnly: true,
    excludeSorting: true,
    excludeInExport: [ExportType.People, ExportType.DigitalActivations],
  },

  [COLUMN_NAMES.UTM_CAMPAIGN]: {
    key: 'utmCampaign',
    pixelOnly: true,
    excludeSorting: true,
    excludeInExport: [ExportType.People, ExportType.DigitalActivations],
  },
  [COLUMN_NAMES.UTM_MEDIUM]: {
    key: 'utmMedium',
    pixelOnly: true,
    excludeSorting: true,
    excludeInExport: [ExportType.People, ExportType.DigitalActivations],
  },
  [COLUMN_NAMES.UTM_SOURCE]: {
    key: 'utmSource',
    pixelOnly: true,
    excludeSorting: true,
    excludeInExport: [ExportType.People, ExportType.DigitalActivations],
  },

  [COLUMN_NAMES.GA_SESSION_ID]: {
    key: 'gaSessionId',
    pixelOnly: true,
    excludeSorting: true,
    excludeInExport: [ExportType.People, ExportType.DigitalActivations],
  },

  [COLUMN_NAMES.TEALIUM_VISITOR_ID]: {
    key: 'tealiumVisitorId',
    pixelOnly: true,
    excludeSorting: true,
    excludeInExport: [ExportType.People, ExportType.Emails, ExportType.Maids, ExportType.DigitalActivations],
  },

  [COLUMN_NAMES.ZIP]: {
    key: 'zip',
    filterField: FilterField.PostalCode,
  },

  // business columns
  ...columnsToRecord([
    COLUMN_NAMES.COMPANY_NAME,
    COLUMN_NAMES.COMPANY_REVENUE,
    COLUMN_NAMES.DEPARTMENT,
    COLUMN_NAMES.JOB_TITLE,
    COLUMN_NAMES.PRIMARY_INDUSTRY,
    COLUMN_NAMES.SENIORITY_LEVEL,
    COLUMN_NAMES.COMPANY_CITY,
    COLUMN_NAMES.COMPANY_STATE,
    COLUMN_NAMES.COMPANY_DOMAIN,
    COLUMN_NAMES.COMPANY_EMPLOYEE_COUNT,
    COLUMN_NAMES.COMPANY_SIC,
  ]),

  [COLUMN_NAMES.BUSINESS_EMAIL]: {
    key: 'businessEmail',
    excludeSorting: true,
  },

  // The key is also the name the API takes as an export field (`ExpandingPeopleField`): one Y/N column per
  // comparison / matching rule in the file.
  [COLUMN_NAMES.MATCHED_BY]: {
    key: 'ComparisonAudiences',
    excludeSorting: true,
    excludeInExport: [ExportType.Journeys],
  },

  [COLUMN_NAMES.MATCHING_RULE]: {
    key: 'MatchingRules',
    excludeSorting: true,
    excludeInExport: [ExportType.Journeys],
  },
};

// Columns only some audiences have; usePeopleColumns adds each back only where it applies.
export const AUDIENCE_SPECIFIC_COLUMNS: ColumnType[] = [COLUMN_NAMES.MATCHED_BY, COLUMN_NAMES.MATCHING_RULE];

export const allColumns: ColumnType[] = Object.values(COLUMN_NAMES).filter(
  (column) => !AUDIENCE_SPECIFIC_COLUMNS.includes(column),
);

export const getReturnTypeForSorting = (ascending: boolean, filterField: FilterField | FilterField[]) => {
  if (Array.isArray(filterField)) {
    return filterField.map((field) => ({ ascending, field }));
  }
  return [{ ascending, field: filterField }];
};
